import { formatCount, formatLocalDate, type StatusTone, toPersianDigits } from '@saay/formatting';
import { type ReactNode, useMemo } from 'react';

import type {
  GeographyArea,
  PhysicalCardCapState,
  PhysicalCardNumberingPolicyVersion,
  PhysicalCardWaitingReason,
  PolicyVersion,
} from '../api.js';
import { QueryState } from '../components/guard.js';
import { SurfaceSection } from '../components/surface-section.js';
import {
  AccordionPanel,
  Banner,
  EmptyState,
  LoadMore,
  Ltr,
  Skeleton,
  StatusChip,
  Table,
} from '../kit.js';
import { NUMBERING_POLICY_TYPE } from '../permissions.js';
import { listStatus, useCitiesOf, usePolicyVersions, useProvinces } from '../queries.js';
import { changeLine, describeChanges, valuesOf } from './physical-card-config.js';
import { policyReasonText } from './wallet-policies.js';

/**
 * Shared pieces of «کارت‌های فیزیکی» (D-117, AC §31.6): the identifiers and labels, the picture of the
 * 16 digits, a version's values (inside a «سابقه» row), and the «سابقه» tab. The tables and their
 * row actions are in `physical-card-cities.tsx` and `physical-card-settings.tsx`.
 */

type Version = PhysicalCardNumberingPolicyVersion;

export function isNumbering(version: PolicyVersion | undefined): version is Version {
  return version?.policyType === NUMBERING_POLICY_TYPE;
}

/** A tag, a series prefix, or a code: an identifier, shown LTR with Persian digits. */
export function Digits({ value }: { readonly value: string }) {
  return <Ltr>{toPersianDigits(value)}</Ltr>;
}

/** «۰۱۱ — گرگان» */
export function cityTagLabel(tag: string, cityNameFa: string): ReactNode {
  return (
    <span className="admin-inline whitespace-nowrap">
      <Digits value={tag} />
      <span>{`— ${cityNameFa}`}</span>
    </span>
  );
}

export const WIDTH_LABEL: Readonly<Record<1 | 2, string>> = {
  1: 'شهرهای یک‌رقمی',
  2: 'شهرهای دورقمی',
};

/** Waiting reasons in plain words (AC §31.7). Unknown values read neutral. */
export const WAITING_REASON_FA: Readonly<Record<PhysicalCardWaitingReason, string>> = {
  CITY_CAP_REACHED: 'سقف شهر پر شده',
  CITY_NOT_CONFIGURED: 'شهرش کد ندارد',
  NO_ACTIVE_SERIES: 'سری فعالی انتخاب نشده',
  SERIAL_EXHAUSTED: 'شماره‌های سری برای شهرش تمام شده',
};

export const CAP_STATE: Readonly<
  Record<PhysicalCardCapState, { readonly label: string; readonly tone: StatusTone }>
> = {
  AT_CAP: { label: 'سقف پر شده', tone: 'danger' },
  NEAR_CAP: { label: 'نزدیک سقف', tone: 'warning' },
  OK: { label: 'عادی', tone: 'success' },
};

export function capStateEntry(value: string): {
  readonly label: string;
  readonly tone: StatusTone;
} {
  return CAP_STATE[value as PhysicalCardCapState] ?? { label: 'نامشخص', tone: 'neutral' };
}

/** Geography names for the ids a version holds (provinces, and the cities of those provinces). */
export function useGeographyNames(provinceIds: readonly string[]) {
  const provinces = useProvinces();
  const cities = useCitiesOf(provinceIds);
  return useMemo(() => {
    const provinceName = new Map<string, string>();
    for (const item of provinces.data ?? []) provinceName.set(item.id, item.nameFa);
    const cityName = new Map<string, string>();
    const cityProvince = new Map<string, string>();
    const citiesOf = new Map<string, readonly GeographyArea[]>();
    provinceIds.forEach((provinceId, index) => {
      const list = cities[index]?.data ?? [];
      citiesOf.set(provinceId, list);
      for (const city of list) {
        cityName.set(city.id, city.nameFa);
        cityProvince.set(city.id, provinceId);
      }
    });
    return {
      cityName,
      cityProvince,
      citiesOf,
      loading: provinces.status === 'pending' || cities.some((query) => query.status === 'pending'),
      provinceName,
      provinces: provinces.data ?? [],
    };
  }, [provinces.data, provinces.status, cities, provinceIds]);
}

// ---------------------------------------------------------------------------------------------
// The 16 digits (AC §31.5): a picture instead of a formula sentence

/** The §31.5 vector `9900000110000017`: series 990000, tag 011, serial 000001, check digit 7. */
const LAYOUT_EXAMPLE: readonly { readonly digits: string; readonly label: string }[] = [
  { digits: '990000', label: 'سری' },
  { digits: '011', label: 'کد شهر' },
  { digits: '000001', label: 'شماره ترتیبی' },
  { digits: '7', label: 'رقم کنترل' },
];

export function NumberLayout() {
  return (
    <SurfaceSection id="physical-card-layout" title="شماره کارت ۱۶ رقم است">
      <figure className="m-0 flex flex-col gap-3">
        <div
          aria-label="نمونه: سری ۹۹۰۰۰۰، کد شهر ۰۱۱، شماره ترتیبی ۰۰۰۰۰۱، رقم کنترل ۷"
          className="flex flex-wrap items-stretch justify-end gap-2"
          dir="ltr"
          role="img"
        >
          {LAYOUT_EXAMPLE.map((segment) => (
            <div
              className="flex min-w-16 flex-col items-center gap-1 rounded-2xl bg-background px-3 py-2"
              key={segment.label}
            >
              <span className="text-lg font-bold tabular-nums tracking-wider" translate="no">
                {toPersianDigits(segment.digits)}
              </span>
              <span className="text-xs text-muted-foreground" dir="rtl">
                {segment.label}
              </span>
            </div>
          ))}
        </div>
        <figcaption className="text-xs text-muted-foreground">
          کد شهر سه یا چهار رقم است: رقم کشور، رقم استان و کد شهر.
        </figcaption>
      </figure>
    </SurfaceSection>
  );
}

// ---------------------------------------------------------------------------------------------
// Values (read)

function tagOf(version: Version, cityId: string, cityProvince: ReadonlyMap<string, string>) {
  const city = version.cities.find((item) => item.cityId === cityId);
  const province = version.provinces.find((item) => item.provinceId === cityProvince.get(cityId));
  return city === undefined || province === undefined
    ? undefined
    : `${province.countryDigit}${province.provinceDigit}${city.cityCode}`;
}

function SeriesValues({ version }: { readonly version: Version }) {
  if (version.series.length === 0) {
    return <p className="admin-muted">هنوز سری‌ای تعریف نشده و کارتی صادر نمی‌شود.</p>;
  }
  return (
    <>
      <ul aria-label="سری‌ها" className="m-0 flex list-none flex-wrap gap-2 p-0">
        {version.series.map((series) => (
          <li
            className="flex items-center gap-2 rounded-full bg-background px-3 py-1.5"
            key={series.prefix}
          >
            <span className="font-bold tabular-nums">
              <Digits value={series.prefix} />
            </span>
            {series.prefix === version.activeSeriesPrefix ? (
              <StatusChip title="فعال" tone="success" />
            ) : null}
          </li>
        ))}
      </ul>
      {version.activeSeriesPrefix === null ? (
        <Banner tone="warning">هیچ سری‌ای فعال نیست؛ کارت جدید صادر نمی‌شود.</Banner>
      ) : null}
    </>
  );
}

function ProvinceValues({
  provinceName,
  version,
}: {
  readonly provinceName: ReadonlyMap<string, string>;
  readonly version: Version;
}) {
  if (version.provinces.length === 0) {
    return <p className="admin-muted">هنوز استانی کد ندارد.</p>;
  }
  return (
    <Table
      caption="کد استان‌ها"
      columns={[
        {
          header: 'استان',
          key: 'province',
          pin: 'start',
          render: (row) => provinceName.get(row.provinceId) ?? '—',
        },
        {
          header: 'کد استان',
          key: 'digits',
          render: (row) => <Digits value={`${row.countryDigit}${row.provinceDigit}`} />,
        },
        {
          header: 'کد شهرها',
          key: 'width',
          render: (row) => WIDTH_LABEL[row.cityCodeWidth],
        },
      ]}
      rowKey={(row) => row.provinceId}
      rows={version.provinces}
    />
  );
}

function CityValues({
  cityName,
  cityProvince,
  version,
}: {
  readonly cityName: ReadonlyMap<string, string>;
  readonly cityProvince: ReadonlyMap<string, string>;
  readonly version: Version;
}) {
  if (version.cities.length === 0) {
    return <p className="admin-muted">هنوز شهری کد ندارد.</p>;
  }
  return (
    <Table
      caption="کد و سقف شهرها"
      columns={[
        {
          header: 'کد و شهر',
          key: 'tag',
          pin: 'start',
          render: (row) => {
            const tag = tagOf(version, row.cityId, cityProvince);
            const name = cityName.get(row.cityId) ?? '—';
            return tag === undefined ? name : cityTagLabel(tag, name);
          },
        },
        {
          header: 'سقف صدور',
          key: 'cap',
          numeric: true,
          render: (row) => formatCount(row.issuanceCap),
        },
      ]}
      rowKey={(row) => row.cityId}
      rows={version.cities}
    />
  );
}

/** The three parts of a version, with small headings (inside a «سابقه» row). */
export function NumberingValues({ version }: { readonly version: Version }) {
  const provinceIds = useMemo(
    () => version.provinces.map((item) => item.provinceId),
    [version.provinces],
  );
  const names = useGeographyNames(provinceIds);
  const parts = [
    { content: <SeriesValues version={version} />, id: 'series', title: 'سری‌ها' },
    {
      content: <ProvinceValues provinceName={names.provinceName} version={version} />,
      id: 'provinces',
      title: 'استان‌ها',
    },
    {
      content: (
        <CityValues cityName={names.cityName} cityProvince={names.cityProvince} version={version} />
      ),
      id: 'cities',
      title: 'شهرها و سقف',
    },
  ];
  return (
    <div className="flex flex-col gap-4">
      {parts.map((part) => (
        <div className="flex flex-col gap-2" key={part.id}>
          <h4 className="text-sm font-bold">{part.title}</h4>
          {part.content}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// «سابقه»

function versionSummary(version: Version): string {
  return `${toPersianDigits(version.series.length)} سری، ${toPersianDigits(version.provinces.length)} استان، ${toPersianDigits(version.cities.length)} شهر`;
}

export function versionTitle(version: Version): string {
  return `نسخهٔ ${toPersianDigits(version.version)}، از ${formatLocalDate(version.effectiveFrom, 'long')}`;
}

/** «سابقه»: every version, newest first; one line says why, who, and what changed. */
export function NumberingHistoryPanel() {
  const versions = usePolicyVersions(NUMBERING_POLICY_TYPE);
  const rows = (versions.data?.pages.flatMap((page) => page.items) ?? []).filter(isNumbering);
  const provinceKey = [
    ...new Set(rows.flatMap((row) => row.provinces.map((item) => item.provinceId))),
  ].join(',');
  const provinceIds = useMemo(
    () => (provinceKey === '' ? [] : provinceKey.split(',')),
    [provinceKey],
  );
  const names = useGeographyNames(provinceIds);
  const ascending = [...rows].sort((left, right) => left.version - right.version);
  const lineOf = new Map(
    ascending.map((row, index) => {
      const previous = ascending[index - 1];
      const changes = describeChanges(
        previous === undefined || previous.version !== row.version - 1
          ? undefined
          : valuesOf(previous),
        valuesOf(row),
        {
          city: (id) => names.cityName.get(id),
          province: (id) => names.provinceName.get(id),
        },
      );
      return [
        row.id,
        [policyReasonText(row.reason), row.createdBy?.displayNameFa ?? 'سیستم', changeLine(changes)]
          .filter((part) => part !== '')
          .join(' · '),
      ] as const;
    }),
  );
  return (
    <SurfaceSection id="physical-card-history" title="سابقهٔ نسخه‌ها">
      <QueryState
        empty={<EmptyState title="نسخه‌ای ثبت نشده است" />}
        error={versions.error}
        isEmpty={rows.length === 0}
        onRetry={() => void versions.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری سابقه…" lines={4} />}
        status={listStatus(versions)}
      >
        <AccordionPanel
          items={[...ascending].reverse().map((row) => ({
            content: <NumberingValues version={row} />,
            key: row.id,
            summary: (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="max-sm:hidden">{versionSummary(row)}</span>
                <StatusChip enumName="policyVersionStatus" value={row.status} />
              </span>
            ),
            title: (
              <span className="flex min-w-0 flex-col items-start gap-0.5 text-start">
                <span>{versionTitle(row)}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {lineOf.get(row.id)}
                </span>
              </span>
            ),
          }))}
          label="سابقهٔ نسخه‌ها"
        />
        <LoadMore
          hasMore={versions.hasNextPage}
          loading={versions.isFetchingNextPage}
          onLoadMore={() => void versions.fetchNextPage()}
        />
      </QueryState>
    </SurfaceSection>
  );
}
